import { useState, useEffect } from 'react';
import { Mail, MailOpen, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { contactAPI } from '../../lib/api';

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    try {
      const response = await contactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await contactAPI.markRead(id);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
          Messages
        </h1>
        <p className="text-muted-foreground">
          Contact form submissions from visitors.
        </p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-background p-4 border border-border animate-pulse">
              <div className="h-6 bg-muted w-1/3 mb-2"></div>
              <div className="h-4 bg-muted w-1/2"></div>
            </div>
          ))}
        </div>
      ) : contacts.length > 0 ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1 space-y-2">
            {contacts.map((contact) => (
              <button
                key={contact.id}
                onClick={() => {
                  setSelectedContact(contact);
                  if (!contact.read) handleMarkRead(contact.id);
                }}
                className={`w-full text-left p-4 border transition-colors ${
                  selectedContact?.id === contact.id 
                    ? 'border-accent bg-accent/5' 
                    : 'border-border bg-background hover:border-accent/50'
                } ${!contact.read ? 'font-medium' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {contact.read ? (
                    <MailOpen className="h-5 w-5 text-muted-foreground mt-0.5" />
                  ) : (
                    <Mail className="h-5 w-5 text-accent mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`truncate ${!contact.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {contact.name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {contact.subject}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedContact ? (
              <div className="bg-background p-6 border border-border">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-foreground mb-1">
                      {selectedContact.subject}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      From: {selectedContact.name} ({selectedContact.email})
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(selectedContact.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="prose prose-slate max-w-none">
                  <p className="text-foreground whitespace-pre-wrap">
                    {selectedContact.message}
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <a 
                    href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                    className="inline-block"
                  >
                    <Button>Reply via Email</Button>
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-background p-12 border border-border text-center">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Select a message to view details
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-background border border-border p-12 text-center">
          <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl text-foreground mb-2">No Messages Yet</h3>
          <p className="text-muted-foreground">
            Contact form submissions will appear here.
          </p>
        </div>
      )}
    </div>
  );
}
